/**
 * 생성기 화면 문구를 한곳에서 만든다 — 입력창·실행 결과·대기 카드·AI 진입 카드.
 * 컴포넌트는 copy 모양만 안다. 번역은 이 훅만 안다.
 */
import { useTranslation } from 'next-i18next';

import {
  type AiGenerateDockCopy,
  type AiGenerateDockOnboardingCopy,
  type AiGenerateDockPendingCardsCopy,
  type AiGenerateDockRunCopy,
  type AiGenerateEntryCopy,
} from '../types/aiGenerateDock.types';

export type AiGenerateDockCopies = {
  copy: AiGenerateDockCopy;
  runCopy: AiGenerateDockRunCopy;
  pendingCopy: AiGenerateDockPendingCardsCopy;
  entryCopy: AiGenerateEntryCopy;
  onboardingCopy: AiGenerateDockOnboardingCopy;
};

export const useAiGenerateDockCopy = (): AiGenerateDockCopies => {
  const { t } = useTranslation(['build', 'common']);

  return {
    copy: {
      sourceTabs: {
        query: t('build:aiGenerateDock.input.sourceTabs.query'),
        file: t('build:aiGenerateDock.input.sourceTabs.file'),
        youtube: t('build:aiGenerateDock.input.sourceTabs.youtube'),
        website: t('build:aiGenerateDock.input.sourceTabs.website'),
      },
      sourceShortLabels: {
        query: t('build:aiGenerateDock.input.sourceShortLabels.query'),
        file: t('build:aiGenerateDock.input.sourceShortLabels.file'),
        youtube: t('build:aiGenerateDock.input.sourceShortLabels.youtube'),
        website: t('build:aiGenerateDock.input.sourceShortLabels.website'),
      },
      sourceTabsLabel: t('build:aiGenerateDock.input.sourceTabsLabel'),
      // 주제 탭도 자료 탭과 같은 안내 「어떤 문제를 만들까요?」(PRD 3-2 · 10/23 지민) — topicPlaceholder 키(「주제나 단원 — 예: …」)는 쓰지 않는다.
      topicPlaceholder: t('build:aiGenerateDock.input.scopePlaceholder'),
      scopePlaceholder: t('build:aiGenerateDock.input.scopePlaceholder'),
      linkPlaceholder: {
        youtube: t('build:aiGenerateDock.input.linkPlaceholder.youtube'),
        website: t('build:aiGenerateDock.input.linkPlaceholder.website'),
      },
      linkSubmit: t('build:aiGenerateDock.input.linkSubmit'),
      attachFile: t('build:aiGenerateDock.input.attachFile'),
      dropFile: t('build:aiGenerateDock.input.dropFile'),
      attachmentStatus: {
        uploading: t('build:aiGenerateDock.input.attachmentStatus.uploading'),
        fetching: t('build:aiGenerateDock.input.attachmentStatus.fetching'),
      },
      removeAttachment: fileName =>
        t('build:aiBuilderChat.attachmentPreview.fileRemove', { fileName }),
      conditionsToggle: t('build:aiGenerateDock.input.conditionsSheetTitle'),
      conditionsSheetTitle: t(
        'build:aiGenerateDock.input.conditionsSheetTitle',
      ),
      conditionsSheetSave: t('common:global.complete.button'),
      countLabel: t('build:aiGenerateDock.input.countLabel'),
      remainingSlots: remaining =>
        t('build:aiGenerateDock.input.remainingSlots', { remaining }),
      quizTypeLabel: t('build:aiGenerateDock.input.quizTypeLabel'),
      quizTypes: {
        multiple: t('build:build.quizType.multiple'),
        subjective: t('build:build.quizType.subjective'),
        ox: t('build:build.quizType.ox'),
        essay: t('build:build.quizType.essay'),
      },
      difficultyLabel: t('build:aiGenerateDock.input.difficultyLabel'),
      difficulties: {
        1: t('build:aiGenerateDock.input.difficulties.easy'),
        2: t('build:aiGenerateDock.input.difficulties.medium'),
        3: t('build:aiGenerateDock.input.difficulties.hard'),
        mixed: t('build:aiGenerateDock.input.difficulties.mixed'),
      },
      submit: t('build:aiGenerateDock.input.submit'),
      close: t('common:global.close.button'),
      running: count => t('build:aiGenerateDock.input.running', { count }),
      preparingSource: t(
        'build:aiBuilderChat.sourcePreparation.readSourceRunning',
      ),
      cancel: t('build:aiGenerateDock.input.cancel'),
      locked: t('build:aiGenerateDock.input.locked'),
    },
    runCopy: {
      usageRemaining: remaining =>
        t('build:aiGenerateDock.run.usageRemaining', { remaining }),
      chatBusy: t('build:aiGenerateDock.run.chatBusy'),
      success: created =>
        t('build:aiGenerateDock.run.success', { count: created }),
      partial: (created, failed) =>
        t('build:aiGenerateDock.run.partial', { created, failed }),
      failed: t('build:aiGenerateDock.run.failed'),
      retry: t('build:aiGenerateDock.run.retry'),
      cancelled: t('build:aiGenerateDock.run.cancelled'),
      quotaExhaustedBanner: t('build:aiGenerateDock.run.quotaExhaustedBanner'),
      upgrade: t('build:aiGenerateDock.run.upgrade'),
      guestBanner: t('build:aiGenerateDock.run.guestBanner'),
      login: t('build:aiGenerateDock.run.login'),
      notGenerated: t('build:aiGenerateDock.run.notGenerated'),
      quotaExceeded: t('build:aiGenerateDock.run.quotaExceeded'),
      retryAfter: seconds =>
        t('build:aiGenerateDock.run.retryAfter', { seconds }),
      tryAgainLater: t('build:build.actionError.waitTry'),
      invalidRequest: t('build:aiGenerateDock.run.invalidRequest'),
      authExpired: t('build:aiGenerateDock.run.authExpired'),
      genericError: t('build:aiGenerateDock.run.genericError'),
      fileTooLarge: limitMb =>
        t('build:aiGenerateDock.run.fileTooLarge', { limitMb }),
      fileTypeNotAllowed: t('build:aiGenerateDock.run.fileTypeNotAllowed'),
      linkInvalid: {
        youtube: t('build:aiGenerateDock.run.linkInvalid.youtube'),
        website: t('build:aiGenerateDock.run.linkInvalid.website'),
      },
    },
    pendingCopy: {
      questionNumber: number =>
        t('build:build.quiz.questionNumber', { number }),
      generating: t('build:aiBuilderChat.generationOverlay.generating'),
      failed: t('build:aiGenerateDock.pending.failed'),
      retry: t('build:aiGenerateDock.pending.retry'),
      dismiss: t('common:global.delete'),
    },
    entryCopy: {
      title: t('build:aiGenerateDock.entry.title'),
      topic: t('build:aiGenerateDock.entry.topic'),
      source: t('build:aiGenerateDock.entry.source'),
      // 첫 예시(광합성의 과정)가 대표 그림 — 나머지는 과목을 달리한다(M7).
      topicSamples: [
        t('build:aiGenerateDock.entry.topicSample'),
        t('build:aiGenerateDock.entry.topicSampleFraction'),
        t('build:aiGenerateDock.entry.topicSampleWaterCycle'),
        t('build:aiGenerateDock.entry.topicSampleSolarSystem'),
      ],
    },
    onboardingCopy: {
      titles: {
        input: t('build:aiGenerateDock.onboarding.input.title'),
        source: t('build:aiGenerateDock.onboarding.source.title'),
        conditions: t('build:aiGenerateDock.onboarding.conditions.title'),
      },
      next: t('common:global.next.button'),
      start: t('build:aiGenerateDock.onboarding.start'),
    },
  };
};
